import React, { useState } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import styled from '@emotion/styled';

import FormInput from '../../../../Form/inputV3/FinalFormInput';
import { DATE, DATE_AFTER_NOW, EMAIL, PASSWORD, REQUIRED } from '../../../../Form/util/validations';
import { calendarParser } from '../../../../Form/util/dateUtils';

import OnboardStep, { OnboardStepProps } from '../../../OnboardStep';

import MEDIA_QUERY_V2 from '../../../../../styles/emotion/mediaQueryV2';
import SPACING from '../../../../../styles/emotion/spacing';
import { Row } from '../../../OnboardContainer/OnboardContainer.styles';
import { StyledTermsPrivacy } from '../../../OnboardStep/OnboardStep.styles';
import {
  SkipDateCheckbox,
  StyledFormDatePicker,
} from '../NamesAndDateStep/NamesAndDateStep.styles';

const StyledOnboardStep = styled(OnboardStep)`
  ${MEDIA_QUERY_V2.DESKTOP} {
    width: 624px;
  }

  ${StyledTermsPrivacy} {
    padding-top: ${SPACING.S16};
    ${MEDIA_QUERY_V2.DESKTOP} {
      padding-top: ${SPACING.XS};
    }
  }
`;

export type QuickSignupStepProps = Pick<OnboardStepProps, 'dek' | 'hed'>;

export const QuickSignupStep = ({
  dek,
  hed = "Almost there—let's lock in your details",
}: QuickSignupStepProps) => {
  const { values } = useFormState();
  const form = useForm();
  const [showPassword, setShowPassword] = useState(false);

  const togglePassword = () => setShowPassword(!showPassword);

  const DATE_REQUIRED = (value?: string | number, allValues?: Partial<{ skip_date?: boolean }>) =>
    allValues?.skip_date ? undefined : REQUIRED(value);

  return (
    <StyledOnboardStep dek={dek} hed={hed} nextButtonText="Continue" showLoginCta>
      <Row format="1fr 1fr" formatMobile="1fr 1fr" gridGapMobile={SPACING.S16}>
        <FormInput
          name="owner_first_name"
          id="owner_first_name"
          label="First name"
          type="text"
          validate={REQUIRED}
        />
        <FormInput
          name="owner_last_name"
          id="owner_last_name"
          label="Last name"
          type="text"
          validate={[REQUIRED]}
        />
      </Row>
      <Row format="1fr 1fr" formatMobile="1fr 1fr" gridGapMobile={SPACING.S16}>
        <FormInput
          name="partner_first_name"
          id="partner_first_name"
          label="Partner's first name"
          type="text"
          validate={[REQUIRED]}
        />
        <FormInput
          name="partner_last_name"
          id="partner_last_name"
          label="Partner's last name"
          type="text"
          validate={[REQUIRED]}
        />
      </Row>
      <Row format="1fr 1fr">
        <StyledFormDatePicker
          name="event_date"
          id="event_date"
          label="Wedding date"
          validate={[DATE, DATE_AFTER_NOW, DATE_REQUIRED]}
          disabled={values.skip_date}
          preventPastMonthsView
          parseDate={calendarParser}
          alignment="vertical"
        />
        <Field
          name="skip_date"
          component={SkipDateCheckbox}
          id="checkbox-skip_date"
          type="checkbox"
          label="We're still deciding"
          onClick={() => form.change('event_date', undefined)}
        />
      </Row>
      <Row format="1fr">
        <FormInput
          name="email"
          id="email"
          type="text"
          label="Email"
          placeholder="Email"
          validate={[REQUIRED, EMAIL]}
        />
      </Row>
      <Row format="1fr">
        <FormInput
          id="password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          label="Password"
          placeholder="Password (8 or more characters)"
          validate={[REQUIRED, PASSWORD]}
          password
          onTogglePasswordVisibility={togglePassword}
        />
      </Row>
    </StyledOnboardStep>
  );
};
